import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { ImageUploaderProps } from '@databox/ember-integrations/lib/components/ImageUploader/ImageUploader';
import ImageUploader from '@databox/ember-integrations/lib/components/ImageUploader/ImageUploader';

import type { ReactIntegrations, Service } from 'webapp-types';
import { Theme } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface Args {
	onImageAdd: (blobUrl: string) => void;
	theme?: Theme | string;
}

export default class CommonImageUploadDropComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('react') declare reactService: Service.React;

	node: HTMLDivElement | null = null;
	reactElement: ReactIntegrations.Root = null;

	currentTheme?: Theme | string;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.currentTheme = this.args.theme;

		this.eventManagerService.on(AppEvent.RERENDER_IMAGE_UPLOAD_DROP_COMPONENT, this, () => {
			if (this.node) {
				this.teardownView();
				this.setupView(this.node);
			}
		});
	}

	@action
	updateTheme(node: HTMLElement) {
		if (this.args.theme !== this.currentTheme) {
			this.currentTheme = this.args.theme;
			// create drop component anew
			this.teardownView();
			this.setupView(node);
		}
	}

	@action
	onPictureDrop(blobUrl: string | null, errorText: string | null) {
		if (blobUrl) {
			this.args.onImageAdd(blobUrl);
		} else {
			this.infolayerService.error(errorText || 'Unknown error');
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
		this.reactElement = this.reactService.create(this.node);

		const props: ImageUploaderProps = {
			onPictureDrop: this.onPictureDrop,
			isDarkThemeActive: this.args.theme === Theme.DARK,
		};

		this.reactService.render(this.reactElement, ImageUploader(props));
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.RERENDER_IMAGE_UPLOAD_DROP_COMPONENT, this);
	}
	<template>
		<div
			class="image-drop-wrapper"
			{{didInsert this.setupView}}
			{{didUpdate this.updateTheme @theme}}
			{{willDestroy this.teardownView}}
		/>
	</template>
}
